import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bell, CalendarPlus, Check, Clock3, Heart, Lock, MessageCircle, Pin, Send, Settings2, ShieldCheck, ThumbsDown, ThumbsUp, Trash2, X } from "lucide-react";
import FooterNav from "../components/FooterNav";
import { hasIdentity, requireIdentity } from "../utils/identityGate";
import {
    checkInEvent,
    cancelEvent,
    createEventDiscussionPost,
    deleteEvent,
    deleteEventDiscussionPost,
    fetchEvent,
    fetchEventDiscussion,
    setEventDiscussionPin,
    updateEventRsvp,
    updateEventDiscussionReaction,
    updateEventSettings,
    type DiscussionMode,
    type EventDiscussionPost,
    type SpaceEvent,
} from "../api/events";

function initials(name: string) {
    return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "B";
}

function compactDate(value: string) {
    const date = new Date(value);
    return {
        month: date.toLocaleDateString(undefined, { month: "short" }),
        dayNumber: date.toLocaleDateString(undefined, { day: "2-digit" }),
    };
}

function time24(value: string) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
}

function eventDate(event: SpaceEvent) {
    const start = new Date(event.startsAt);
    return {
        ...compactDate(event.startsAt),
        day: start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }),
        time: `${time24(event.startsAt)} - ${time24(event.endsAt)}`,
        compact: `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} / ${time24(event.startsAt)}`,
    };
}

function durationLabel(event: SpaceEvent) {
    const start = new Date(event.startsAt);
    const end = new Date(event.endsAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return "";
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours && rest) return `${hours}h ${rest}m`;
    if (hours) return `${hours}h`;
    return `${minutes}m`;
}

function formatCountdown(totalSeconds: number) {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    const two = (value: number) => value.toString().padStart(2, "0");
    if (days) return `${days}d ${two(hours)}h ${two(minutes)}m ${two(rest)}s`;
    if (hours) return `${hours}h ${two(minutes)}m ${two(rest)}s`;
    if (minutes) return `${minutes}m ${two(rest)}s`;
    return `${rest}s`;
}

function relativeEventClock(event: SpaceEvent, now: number) {
    const start = new Date(event.startsAt).getTime();
    const end = new Date(event.endsAt).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return "";
    if (event.lifecycle === "cancelled") return "Cancelled";
    if (now < start) return `Starts in ${formatCountdown((start - now) / 1000)}`;
    if (now < end) return `Ends in ${formatCountdown((end - now) / 1000)}`;
    return "Ended";
}

function lifecycleCopy(event: SpaceEvent) {
    if (event.lifecycle === "live") return { label: "Live Now", tone: "live", body: "The room is open. Join the conversation while the gathering is active." };
    if (event.lifecycle === "scheduled") return { label: "Upcoming", tone: "scheduled", body: "The room opens at the scheduled start time." };
    if (event.lifecycle === "cancelled") return { label: "Cancelled", tone: "ended", body: "This event was cancelled and is preserved for context." };
    return { label: "Preserved", tone: "ended", body: "This event has ended. Its room remains as a quiet archive." };
}

function eventAccent(event: SpaceEvent) {
    const space = event.space as SpaceEvent["space"] & {
        color?: string;
        accent?: string;
        accentColor?: string;
        themeColor?: string;
    };
    const direct = space?.color || space?.accentColor || space?.accent || space?.themeColor;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    return "#22d3ee";
}

function alpha(hex: string, fallback: string) {
    if (/^#[0-9a-f]{6}$/i.test(hex)) return `${hex}${fallback}`;
    return `color-mix(in srgb, ${hex} 18%, transparent)`;
}

function downloadCalendar(event: SpaceEvent) {
    const stamp = (value: string) => new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const escape = (value: string) => value.replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
    const content = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Blackspace//Events//EN", "BEGIN:VEVENT", `UID:${event.id}@blackspace.live`, `DTSTART:${stamp(event.startsAt)}`, `DTEND:${stamp(event.endsAt)}`, `SUMMARY:${escape(event.title)}`, `DESCRIPTION:${escape(event.description || "")}`, `LOCATION:${escape(event.space?.name || "Blackspace")}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([content], { type: "text/calendar" }));
    link.download = `${event.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "blackspace-event"}.ics`;
    link.click();
    URL.revokeObjectURL(link.href);
}

function AvatarMark({ name, src, size = "md" }: { name: string; src?: string; size?: "sm" | "md" | "lg" }) {
    const className = size === "sm" ? "bs-event-avatar is-sm" : size === "lg" ? "bs-event-avatar is-lg" : "bs-event-avatar";
    return src ? <img src={src} alt="" className={className} /> : <span className={className}>{initials(name)}</span>;
}

function DiscussionPost({ post, replies, canReply, onReply, onReact, onPin, onDelete }: { post: EventDiscussionPost; replies: EventDiscussionPost[]; canReply: boolean; onReply: (post: EventDiscussionPost) => void; onReact: (post: EventDiscussionPost, type: "like" | "love" | "dislike") => void; onPin: (post: EventDiscussionPost) => void; onDelete: (post: EventDiscussionPost) => void }) {
    const author = post.author.displayName || `@${post.author.username}`;
    const reactions = post.reactionCounts || { like: 0, love: 0, dislike: 0, total: 0 };
    const reactionActions: Array<{ type: "love" | "like" | "dislike"; label: string; count: number; icon: React.ReactNode }> = [
        { type: "love", label: "Love", count: reactions.love || 0, icon: <Heart size={15} fill={post.myReaction === "love" ? "currentColor" : "none"} /> },
        { type: "like", label: "Like", count: reactions.like || 0, icon: <ThumbsUp size={15} fill={post.myReaction === "like" ? "currentColor" : "none"} /> },
        { type: "dislike", label: "Dislike", count: post.canSeeDislikes ? reactions.dislike || 0 : 0, icon: <ThumbsDown size={15} fill={post.myReaction === "dislike" ? "currentColor" : "none"} /> },
    ];
    return (
        <article className={`bs-event-discussion-card ${post.kind === "announcement" ? "is-announcement" : ""}`}>
            <div className="bs-event-discussion-head">
                <div className="bs-event-discussion-author">
                    <AvatarMark name={author} src={post.author.avatarUrl} />
                    <div className="min-w-0">
                        <div className="bs-event-discussion-name-row">
                            <span className="bs-event-discussion-name">{author}</span>
                            {post.kind === "announcement" && <span className="bs-event-mini-chip">Announcement</span>}
                            {post.pinned && <span className="bs-event-pin-chip"><Pin size={12} /> Pinned</span>}
                        </div>
                        <time className="bs-event-discussion-time">{new Date(post.createdAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</time>
                    </div>
                </div>
                <div className="bs-event-discussion-actions">
                    {post.canPin && <button onClick={() => onPin(post)} title={post.pinned ? "Unpin" : "Pin"} aria-label={post.pinned ? "Unpin post" : "Pin post"}><Pin size={15} /></button>}
                    {post.canDelete && <button onClick={() => onDelete(post)} title="Delete" aria-label="Remove post" className="is-danger"><Trash2 size={15} /></button>}
                </div>
            </div>
            <p className="bs-event-discussion-copy">{post.content}</p>
            <div className="bs-event-discussion-foot">
                <div className="bs-event-reaction-row" aria-label="Reactions">
                    {reactionActions.map((action) => {
                        const active = post.myReaction === action.type;
                        return (
                            <button key={action.type} type="button" onClick={() => onReact(post, action.type)} disabled={!canReply} className={`bs-event-reaction-command ${active ? "is-active" : ""}`} aria-pressed={active} title={active ? `Remove ${action.label.toLowerCase()}` : action.label} aria-label={active ? `Remove ${action.label.toLowerCase()}` : action.label}>
                                {action.icon}
                                {action.count > 0 && <span className="bs-event-reaction-count">{action.count}</span>}
                            </button>
                        );
                    })}
                </div>
                {canReply && <button type="button" onClick={() => onReply(post)} className="bs-event-reply-command">Reply</button>}
            </div>
            {replies.length > 0 && (
                <div className="bs-event-replies">
                    {replies.map((reply) => (
                        <div key={reply.id} className="bs-event-reply">
                            <AvatarMark name={reply.author.displayName || `@${reply.author.username}`} src={reply.author.avatarUrl} size="sm" />
                            <div className="min-w-0 flex-1">
                                <div className="bs-event-reply-head">
                                    <span>{reply.author.displayName || `@${reply.author.username}`}</span>
                                    {reply.canDelete && <button onClick={() => onDelete(reply)} title="Delete reply" aria-label="Remove reply"><Trash2 size={12} /></button>}
                                </div>
                                <p>{reply.content}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </article>
    );
}

export default function EventDetail() {
    const isAnonymous = !hasIdentity();
    const { spaceId = "", eventId = "" } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const composerRef = useRef<HTMLFormElement | null>(null);
    const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
    const boundaryRefreshRef = useRef("");
    const [event, setEvent] = useState<SpaceEvent | null>(null);
    const [posts, setPosts] = useState<EventDiscussionPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [replyTo, setReplyTo] = useState<EventDiscussionPost | null>(null);
    const [announcement, setAnnouncement] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [clockNow, setClockNow] = useState(() => Date.now());
    const messageLimit = replyTo ? 337 : 237;

    const resizeMessageInput = useCallback(() => {
        const input = messageInputRef.current;
        if (!input) return;
        input.style.height = "auto";
        input.style.height = `${Math.min(input.scrollHeight, 170)}px`;
    }, []);

    const load = useCallback(async () => {
        if (!eventId) return;
        try {
            const [nextEvent, nextDiscussion] = await Promise.all([fetchEvent(eventId), fetchEventDiscussion(eventId)]);
            setEvent(nextEvent);
            setPosts(nextDiscussion.posts);
            setError("");
        } catch (caught: any) {
            setError(caught?.message || "Could not open this event.");
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        document.documentElement.classList.add("bs-event-detail-active");
        document.body.classList.add("bs-event-detail-active");
        return () => {
            document.documentElement.classList.remove("bs-event-detail-active");
            document.body.classList.remove("bs-event-detail-active");
        };
    }, []);
    useEffect(() => {
        if (event?.lifecycle !== "live") return;
        const timer = window.setInterval(load, 20000);
        return () => window.clearInterval(timer);
    }, [event?.lifecycle, load]);
    useEffect(() => {
        const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    useEffect(() => {
        if (!event) return;
        const start = new Date(event.startsAt).getTime();
        const end = new Date(event.endsAt).getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end)) return;
        const crossedStart = event.lifecycle === "scheduled" && clockNow >= start;
        const crossedEnd = event.lifecycle === "live" && clockNow >= end;
        if (!crossedStart && !crossedEnd) return;
        const key = `${event.id}:${crossedStart ? "start" : "end"}`;
        if (boundaryRefreshRef.current === key) return;
        boundaryRefreshRef.current = key;
        load();
    }, [clockNow, event, load]);
    useEffect(() => { resizeMessageInput(); }, [message, replyTo, resizeMessageInput]);
    useEffect(() => {
        setMessage((current) => current.slice(0, replyTo ? 337 : 237));
    }, [replyTo]);

    const roots = useMemo(() => posts.filter((post) => !post.parentId), [posts]);
    const replyMap = useMemo(() => posts.reduce<Record<string, EventDiscussionPost[]>>((map, post) => { if (post.parentId) (map[post.parentId] ||= []).push(post); return map; }, {}), [posts]);

    async function setRsvp(status: "going" | "interested" | "none") {
        if (isAnonymous) return void requireIdentity("RSVP to this event");
        if (!event) return;
        setBusy(true);
        try {
            const updated = await updateEventRsvp(event.id, status, status === "going" ? [1440, 60, 10] : [60]);
            setEvent(updated);
        } catch (caught: any) { setError(caught?.message || "Could not update RSVP."); } finally { setBusy(false); }
    }

    async function submitPost(formEvent: FormEvent) {
        formEvent.preventDefault();
        if (isAnonymous) return void requireIdentity("join the event discussion");
        if (!event || !message.trim()) return;
        setBusy(true);
        try {
            const post = await createEventDiscussionPost(event.id, { content: message.slice(0, messageLimit).trim(), kind: announcement ? "announcement" : "discussion", parentId: replyTo?.id });
            setPosts((current) => [post, ...current]);
            setMessage(""); setReplyTo(null); setAnnouncement(false);
        } catch (caught: any) { setError(caught?.message || "Could not post to the discussion."); } finally { setBusy(false); }
    }

    async function pin(post: EventDiscussionPost) {
        if (!event) return;
        try { const updated = await setEventDiscussionPin(event.id, post.id, !post.pinned); setPosts((current) => current.map((item) => item.id === updated.id ? updated : item)); } catch (caught: any) { setError(caught?.message || "Could not update the pin."); }
    }

    async function remove(post: EventDiscussionPost) {
        if (!event || !window.confirm("Remove this post from the event discussion?")) return;
        try { await deleteEventDiscussionPost(event.id, post.id); setPosts((current) => current.filter((item) => item.id !== post.id && item.parentId !== post.id)); } catch (caught: any) { setError(caught?.message || "Could not remove the post."); }
    }

    function handleMessageChange(value: string) {
        setMessage(value.slice(0, messageLimit));
    }

    async function reactToPost(post: EventDiscussionPost, type: "like" | "love" | "dislike") {
        if (isAnonymous) return void requireIdentity("react in this event");
        if (!event) return;
        try {
            const updated = await updateEventDiscussionReaction(event.id, post.id, { type, active: post.myReaction !== type });
            setPosts((current) => current.map((item) => item.id === updated.id ? updated : item));
        } catch (caught: any) {
            setError(caught?.message || "Could not update reaction.");
        }
    }

    async function changeMode(mode: DiscussionMode) {
        if (!event) return;
        setBusy(true);
        try { setEvent(await updateEventSettings(event.id, { mode })); } catch (caught: any) { setError(caught?.message || "Could not change discussion controls."); } finally { setBusy(false); }
    }

    async function checkIn() {
        if (!event) return;
        setBusy(true);
        try { setEvent(await checkInEvent(event.id)); } catch (caught: any) { setError(caught?.message || "Could not update check-in."); } finally { setBusy(false); }
    }

    async function cancelCurrentEvent() {
        if (!event || !window.confirm("Cancel this event? Attendees will be notified.")) return;
        setBusy(true);
        try {
            const result = await cancelEvent(event.id);
            setEvent(result.event);
            setSettingsOpen(false);
        } catch (caught: any) { setError(caught?.message || "Could not cancel the event."); } finally { setBusy(false); }
    }

    async function deleteCurrentEvent() {
        if (!event || !window.confirm("Delete this event and its public entry?")) return;
        setBusy(true);
        try {
            const result = await deleteEvent(event.id);
            if (result.deleted) navigate(`/spaces/${spaceId}`, { replace: true, state: location.state });
            else if (result.event) setEvent(result.event);
        } catch (caught: any) { setError(caught?.message || "Could not delete the event."); } finally { setBusy(false); }
    }

    function backToSpace() {
        navigate(`/spaces/${spaceId}`, { replace: true, state: location.state });
    }

    if (loading) return <div className="bs-event-page min-h-screen bg-black text-white"><div className="bs-event-loading mx-auto max-w-6xl px-4 py-16"><div /></div><FooterNav /></div>;
    if (!event) return <div className="bs-event-page min-h-screen bg-black px-4 py-16 text-white"><div className="mx-auto max-w-xl rounded-2xl border border-red-900/50 bg-red-950/20 p-6"><h1 className="font-black">Event unavailable</h1><p className="mt-2 text-sm text-red-200">{error}</p><button onClick={backToSpace} className="mt-5 font-bold text-white">Back to Space</button></div><FooterNav /></div>;

    const accent = eventAccent(event);
    const pageStyle = {
        "--bs-event-accent": accent,
        "--bs-event-accent-soft": alpha(accent, "22"),
        "--bs-event-accent-mist": alpha(accent, "12"),
        "--bs-event-accent-line": alpha(accent, "66"),
    } as React.CSSProperties;
    const when = eventDate(event);
    const eventLive = event.lifecycle === "live";
    const eventScheduled = event.lifecycle === "scheduled";
    const postingAllowed = Boolean(eventLive && event.canDiscuss && event.discussion.isOpen);
    const ended = event.lifecycle === "ended" || event.lifecycle === "cancelled";
    const hostView = Boolean(event.canEdit || event.canManageDiscussion);
    const stage = lifecycleCopy(event);
    const duration = durationLabel(event);
    const relativeTime = relativeEventClock(event, clockNow);
    const hostStatusLabel = `Host: ${event.creator.username || event.creator.displayName || "Host"}`;

    return (
        <div className="bs-event-page min-h-screen overflow-x-hidden bg-black pb-28 text-white" style={pageStyle}>
            <header className="bs-event-topbar">
                <div className="bs-event-topbar-inner">
                    <button onClick={backToSpace} className="bs-event-back-button">
                        <ArrowLeft size={18} />
                        <span>{event.space.name}</span>
                    </button>
                    <div className="bs-event-topbar-actions">
                        <button onClick={() => downloadCalendar(event)} aria-label="Add to calendar" title="Add to calendar" className="bs-event-icon-button"><CalendarPlus size={17} /></button>
                        {event.canManageDiscussion && eventScheduled && <button onClick={() => setSettingsOpen((open) => !open)} aria-expanded={settingsOpen} aria-label="Event controls" title="Event controls" className={`bs-event-icon-button ${settingsOpen ? "is-active" : ""}`}><Settings2 size={17} /></button>}
                    </div>
                </div>
            </header>

            <main className="bs-event-main">
                {error && <p role="alert" className="bs-event-alert">{error}</p>}

                <section className={`bs-event-stage is-${stage.tone}`}>
                    <div className="bs-event-stage-orb" aria-hidden="true" />
                    <div className="bs-event-date-card">
                        <span>{when.month}</span>
                        <strong>{when.dayNumber}</strong>
                        <small>{when.time}</small>
                        <em aria-live="polite">{relativeTime || duration || "Scheduled"}</em>
                    </div>
                    <div className="bs-event-stage-copy">
                        <div className="bs-event-status-row">
                            <span className={`bs-event-status-chip is-${stage.tone}`}>{eventLive && <span className="bs-event-live-signal" />} {stage.label}</span>
                            {event.type && <span className="bs-event-quiet-chip">{event.type}</span>}
                            <span className="bs-event-quiet-chip bs-event-host-status">{hostStatusLabel}</span>
                        </div>
                        <h1>{event.title}</h1>
                        {event.description && <p className="bs-event-description">{event.description}</p>}
                        <p className="bs-event-stage-note">{stage.body}</p>

                        {!ended && !hostView && (
                            <div className="bs-event-action-row">
                                <button disabled={busy} onClick={() => setRsvp(event.myRsvp === "going" || event.myRsvp === "waitlisted" ? "none" : "going")} className={`bs-event-primary-command ${event.myRsvp === "going" || event.myRsvp === "waitlisted" ? "is-selected" : ""}`}>{event.myRsvp === "waitlisted" ? "Leave waitlist" : event.myRsvp === "going" ? "Going" : "Join event"}</button>
                                <button disabled={busy} onClick={() => setRsvp(event.myRsvp === "interested" ? "none" : "interested")} className={`bs-event-secondary-command ${event.myRsvp === "interested" ? "is-selected" : ""}`}>Interested</button>
                                {eventLive && event.myRsvp === "going" && <button disabled={busy} onClick={checkIn} className={`bs-event-secondary-command ${event.checkedInAt ? "is-selected" : ""}`}><Check size={15} />{event.checkedInAt ? "Checked in" : "Check in"}</button>}
                            </div>
                        )}
                        {!ended && !hostView && event.myRsvp && <p className="bs-event-reminder-note"><Bell size={13} /> Reminders are set for this event.</p>}
                    </div>
                </section>

                {ended && (
                    <div className="bs-event-archive-note">
                        <Lock size={16} />
                        <p>This event is preserved exactly as it stood when the room closed.</p>
                    </div>
                )}

                {settingsOpen && event.canManageDiscussion && eventScheduled && (
                    <section className="bs-event-host-controls">
                        <div className="bs-event-host-controls-head">
                            <div>
                                <h2><ShieldCheck size={17} /> Host controls</h2>
                                <p>Choose who can speak when the room opens.</p>
                            </div>
                            <button type="button" onClick={() => setSettingsOpen(false)} aria-label="Close host controls"><X size={16} /></button>
                        </div>
                        <div className="bs-event-mode-grid">
                            {(["open", "attendees", "announcements", "locked"] as DiscussionMode[]).map((mode) => {
                                const labels: Record<DiscussionMode, string> = { open: "Everyone", attendees: "Going only", announcements: "Host notes", locked: "Locked" };
                                return <button key={mode} disabled={busy} onClick={() => changeMode(mode)} aria-pressed={event.discussion.mode === mode} className={event.discussion.mode === mode ? "is-selected" : ""}>{labels[mode]}</button>;
                            })}
                        </div>
                        <div className="bs-event-danger-row">
                            {event.canCancel && !event.isCancelled && <button disabled={busy} onClick={cancelCurrentEvent}>Cancel event</button>}
                            {event.canDelete && <button disabled={busy} onClick={deleteCurrentEvent}>Delete event</button>}
                        </div>
                    </section>
                )}

                <div className="bs-event-room-layout">
                    <section className="bs-event-conversation-panel" aria-labelledby="event-discussion-title">
                        <div className="bs-event-section-head">
                            <div>
                                <h2 id="event-discussion-title">Room</h2>
                            </div>
                            <span>{!eventLive && !ended ? <Clock3 size={13} /> : event.discussion.mode === "locked" ? <Lock size={13} /> : <MessageCircle size={13} />}{!eventLive && !ended ? "Opens at start" : event.discussion.mode === "attendees" ? "Going only" : event.discussion.mode === "announcements" ? "Host notes" : event.discussion.mode}</span>
                        </div>

                        {postingAllowed && (
                            <form ref={composerRef} onSubmit={submitPost} className="bs-event-room-composer">
                                {replyTo && <div className="bs-event-replying-to"><span>Replying to {replyTo.author.displayName || `@${replyTo.author.username}`}</span><button type="button" onClick={() => setReplyTo(null)}>Cancel</button></div>}
                                <textarea ref={messageInputRef} value={message} onChange={(e) => handleMessageChange(e.target.value)} maxLength={messageLimit} rows={1} aria-label="Write to the event room" placeholder={replyTo ? "Reply to the room..." : event.discussion.mode === "announcements" ? "Post an announcement..." : "Add to the event conversation..."} className="bs-event-room-textarea" />
                                <div className="bs-event-composer-foot">
                                    {event.canManageDiscussion ? <button type="button" onClick={() => setAnnouncement((current) => !current)} className={`bs-event-announcement-toggle ${announcement ? "is-active" : ""}`} aria-pressed={announcement}>Announcement</button> : <span>{event.discussion.slowModeSeconds ? `${event.discussion.slowModeSeconds}s slow mode` : "Space members can reply"}</span>}
                                    <span className="bs-event-message-count">{message.length}/{messageLimit}</span>
                                    <button disabled={busy || !message.trim()} className="bs-event-send-command"><Send size={14} /> Post</button>
                                </div>
                            </form>
                        )}
                        {!postingAllowed && !ended && <div className="bs-event-room-locked"><Lock size={18} />{!eventLive ? "The room opens when this gathering starts." : event.discussion.mode === "locked" || !event.discussion.isOpen ? "This discussion is currently locked." : event.discussion.mode === "attendees" ? "Join the event to take part in this discussion." : "Join the Space to take part in this discussion."}</div>}

                        <div className="bs-event-discussion-list">
                            {roots.map((post) => (
                                <DiscussionPost
                                    key={post.id}
                                    post={post}
                                    replies={replyMap[post.id] || []}
                                    canReply={postingAllowed}
                                    onReply={(target) => {
                                        setReplyTo(target);
                                        window.requestAnimationFrame(() => composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
                                    }}
                                    onReact={reactToPost}
                                    onPin={pin}
                                    onDelete={remove}
                                />
                            ))}
                            {!roots.length && <div className="bs-event-empty-room"><MessageCircle /><h3>The room is quiet</h3><p>The first useful thought can set the tone.</p></div>}
                        </div>
                    </section>
                </div>
            </main>
            <FooterNav />
        </div>
    );
}
